import * as React from 'react';
import { ContextMenu } from 'primereact/contextmenu';
import { Button } from 'primereact/button';
import { PDATreeNode } from '../../helpers/tree';
import { DocTemplateFolderTypeVals } from '../../helpers/constants/modelValues/DocTemplateFolderTypeVals';
import { TreeComponent, TreeComponentProps } from './TreeComponent';
import { MenuItem } from 'primereact/menuitem';
import { ProjectDocFolderSubTypes } from '../../helpers/mapping/ProjectDocFolderSubTypes';
import { TreeDragDropEvent, TreeEventNodeEvent } from 'primereact/tree';
import { ProjectDoc } from '../../pages/projectDocs/models/ProjectDoc';
import { ProjectDocTypeVals } from '../../helpers/mapping/ProjectDocTypes';
import { normalizeProjectType, ProjectTypeValues } from '../../helpers/mapping/ProjectTypes';
import { MenuItemValues } from '../../helpers/constants/systemValues/MenuItemValues';
import { Tooltip } from 'primereact/tooltip';
import { DocumentTypeVals } from '../../helpers/mapping/DocumentTypeVals';
import { useIsMobile } from '../../helpers/hooks/useIsMobile';
import { CriticalNoteModeEnum } from '../../pages/notes/models/WorkflowCriticalNoteResult';
import { ProjectDocFolderStatuses } from '../../helpers/mapping/ProjectDocFolderStatuses';
import { labelColor } from '../synced/ui/ProgressBar/ProgressBar';
import { ALL_SYSTEM_FOLDER_TITLES } from '../../helpers/constants/systemValues/SectionSystemFolderTitles';

interface TreeEditorProps extends TreeComponentProps {
    onMenu: (node: PDATreeNode) => void;
    onPositionsChanged: (ids: number[]) => void;
    leafMenuItems: ContextMenuItem[];
    nodeMenuItems: ContextMenuItem[];
    workTypeMenuItems: ContextMenuItem[];
    actMenuItems: ContextMenuItem[];
    actDocumentMenuItems: ContextMenuItem[];
    actDocumentSectionMenuItems: ContextMenuItem[];
    actSectionMenuItems: ContextMenuItem[];
    canEditNode?: (node: PDATreeNode) => boolean;
    projectDoc?: ProjectDoc;
    afterNodeValue?: (node: PDATreeNode) => React.ReactNode;
    criticalFolderMap?: Map<number, CriticalNoteModeEnum>;
    criticalBeforeFileIcon?: boolean;
    cypherMatchesProgress?: boolean;
}

export interface ContextMenuItem extends MenuItem {
    type: number;
}

export const TreeEditor: React.FC<TreeEditorProps> = (props) => {
    const { criticalBeforeFileIcon, cypherMatchesProgress, ...treeComponentProps } = props;
    const isMobile = useIsMobile(900);
    const cmNodeRef = React.useRef<ContextMenu>(null);
    // const cmNodeChildNodeRef = React.useRef<ContextMenu>(null);
    // const cmNodeChildTomRef = React.useRef<ContextMenu>(null);

    const cmWorkTypeRef = React.useRef<ContextMenu>(null);
    const cmLeafRef = React.useRef<ContextMenu>(null);
    const cmActRef = React.useRef<ContextMenu>(null);
    const cmActDocumentRef = React.useRef<ContextMenu>(null);
    const cmActDocumentSectionRef = React.useRef<ContextMenu>(null);
    const cmActSectionRef = React.useRef<ContextMenu>(null);

    const {
        treeNodes,
        leafMenuItems,
        nodeMenuItems,
        workTypeMenuItems,
        actMenuItems,
        actDocumentMenuItems,
        actDocumentSectionMenuItems,
        actSectionMenuItems,
        onMenu,
        onPositionsChanged,
        canEditNode,
        projectDoc,
        afterNodeValue,
        criticalFolderMap
    } = props;

    const onContextMenuClick: (currentEvent: any, node: PDATreeNode) => void = (currentEvent, node) => {
        onMenu(node);
        cmLeafRef.current?.hide(currentEvent);
        cmNodeRef.current?.hide(currentEvent);
        cmWorkTypeRef.current?.hide(currentEvent);
        cmActRef.current?.hide(currentEvent);
        cmActDocumentRef.current?.hide(currentEvent);
        cmActDocumentSectionRef.current?.hide(currentEvent);
        cmActSectionRef.current?.hide(currentEvent);

        if (
            node.FolderTypeId === DocTemplateFolderTypeVals.Section &&
            node.ParentId == null &&
            (node as any).data?.StructureType != null
        ) {
            cmWorkTypeRef.current?.show(currentEvent);
        } else if (node.FolderTypeId === DocTemplateFolderTypeVals.Section) {
            cmNodeRef.current?.show(currentEvent);
        } else if (node.FolderTypeId === DocTemplateFolderTypeVals.Tom) {
            cmLeafRef.current?.show(currentEvent);
        }
        if (node.FolderTypeId === DocTemplateFolderTypeVals.Act) {
            cmActRef.current?.show(currentEvent);
        }
        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActDocument) {
            cmActDocumentRef.current?.show(currentEvent);
        }
        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActDocumentSection) {
            cmActDocumentSectionRef.current?.show(currentEvent);
        }
        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActSection) {
            cmActSectionRef.current?.show(currentEvent);
        }
    };

    const nodeVal = (node: PDATreeNode, showCritical = true) => {
        const mode = criticalFolderMap?.get(Number(node.key));
        const criticalStyle =
            isMobile && mode
                ? {
                      color:
                          mode === CriticalNoteModeEnum.Active
                              ? 'var(--danger-color)'
                              : mode === CriticalNoteModeEnum.Warranty
                                ? 'var(--accent-orange)'
                                : 'var(--text-tertiary)'
                  }
                : undefined;

        return node.bagdeValue ? (
            <div className="d-inline" style={criticalStyle}>
                {Boolean(node.Cypher) && !node['isInGeneralSection'] && (
                    <React.Fragment>
                        <span className="cypher px-1">{node.Cypher}</span>&nbsp;
                    </React.Fragment>
                )}
                {Boolean(node.SubTypeId == ProjectDocFolderSubTypes.Change) && (
                    <React.Fragment>
                        <span className="cypher px-1">изм-{node['OrderNumber']}</span>&nbsp;
                    </React.Fragment>
                )}
                {node.label}
                &nbsp;
                {(node.FolderTypeId === DocTemplateFolderTypeVals.Tom ||
                    node.FolderTypeId === DocTemplateFolderTypeVals.Act ||
                    node.FolderTypeId === DocTemplateFolderTypeVals.ActDocument) &&
                    node.data?.DocumentTypeId != DocumentTypeVals.Registry && (
                        <span className={node.bagdeClass}>{node.bagdeValue}</span>
                    )}
                &nbsp;
                {showCritical && criticalBadge(node)}
            </div>
        ) : (
            <React.Fragment>
                {Boolean(node.Cypher) && !node['isInGeneralSection'] && (
                    <React.Fragment>
                        <span className="cypher px-1">{node.Cypher}</span>&nbsp;
                    </React.Fragment>
                )}
                {Boolean(node.SubTypeId == ProjectDocFolderSubTypes.Change) && (
                    <React.Fragment>
                        <span className="cypher px-1">изм-{node['OrderNumber']}</span>&nbsp;
                    </React.Fragment>
                )}
                <span style={criticalStyle}>{node.label}</span>
                &nbsp;
                {showCritical && criticalBadge(node)}
            </React.Fragment>
        );
    };

    const updateMenuItemsVisibility = (node: any) => {
        const projectType = normalizeProjectType(projectDoc?.Project?.Directorate);
        const canCreateTom =
            projectDoc?.DocTypeId == ProjectDocTypeVals.Executive &&
            projectType === ProjectTypeValues.CapitalConstruction;
        const canCreateAct =
            projectDoc?.DocTypeId == ProjectDocTypeVals.Executive && projectType === ProjectTypeValues.Reconstruction;

        if (
            node.FolderTypeId === DocTemplateFolderTypeVals.Section &&
            node.ParentId == null &&
            node.data?.StructureType != null
        ) {
            workTypeMenuItems
                ?.filter((x) => x.type === MenuItemValues.AddTom)
                .forEach((x) => (x.visible = canCreateTom));
            workTypeMenuItems
                ?.filter((x) => x.type === MenuItemValues.AddAct)
                .forEach((x) => (x.visible = canCreateAct));
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.Act) {
            workTypeMenuItems
                ?.filter((x) => x.type === MenuItemValues.AddActDocument)
                .forEach((x) => (x.visible = canCreateAct));
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActDocumentSection) {
            workTypeMenuItems
                ?.filter((x) => x.type === MenuItemValues.AddActDocument)
                .forEach((x) => (x.visible = canCreateAct));
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActSection) {
            workTypeMenuItems
                ?.filter((x) => x.type === MenuItemValues.AddAct)
                .forEach((x) => (x.visible = canCreateAct));
        }
    };

    const editNodeBtn = (node) => {
        if (canEditNode) {
            if (!canEditNode(node)) return null;
        }

        updateMenuItemsVisibility(node);

        if (node.FolderTypeId === DocTemplateFolderTypeVals.Tom) {
            if (leafMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.Section) {
            if (nodeMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (
            node.FolderTypeId === DocTemplateFolderTypeVals.Section &&
            node.ParentId == null &&
            node.data?.StructureType != null
        ) {
            if (workTypeMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.Act) {
            if (actMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActDocument) {
            if (actDocumentMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActDocumentSection) {
            if (actDocumentSectionMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        if (node.FolderTypeId === DocTemplateFolderTypeVals.ActSection) {
            if (actSectionMenuItems?.some((x) => x.visible === undefined || x.visible == true) != true) {
                return null;
            }
        }

        return (
            <Button
                icon="fa fa-ellipsis-v"
                style={{ margin: 0, padding: 0, height: 'auto' }}
                className="p-button-text"
                onClick={(e) => {
                    onContextMenuClick(e, node);
                }}
            />
        );
    };

    const togglerOptsRef = React.useRef<Map<string, { expanded: boolean; onClick: (e: React.MouseEvent) => void }>>(
        new Map()
    );

    const togglerTemplate = (node: any, options: any) => {
        togglerOptsRef.current.set(String(node.key), {
            expanded: options.expanded,
            onClick: options.onClick
        });
        return null;
    };

    const defaultNodeTemplate = (node: any) => {
        const isTomOrAct =
            (node.FolderTypeId === DocTemplateFolderTypeVals.Tom ||
                node.FolderTypeId === DocTemplateFolderTypeVals.Act ||
                node.FolderTypeId === DocTemplateFolderTypeVals.ActDocument) &&
            node.data?.DocumentTypeId != DocumentTypeVals.Registry;
        const showAfterNodeValue = afterNodeValue && isTomOrAct;
        const cypherColor =
            cypherMatchesProgress &&
            showAfterNodeValue &&
            node.data?.StatusId !== ProjectDocFolderStatuses.OrderDocument &&
            node.data?.DocumentTypeId !== DocumentTypeVals.ResponsiblePersonOrder
                ? isMobile
                    ? 'var(--text-muted)'
                    : labelColor(Math.max(0, Math.min(100, Math.round(node.data?.CompletionPercentage ?? 0))))
                : undefined;
        const oldStyle = { color: node?.isOldProjectDocFolder ? '#B0B0B0' : '' };
        const nodeHasCypher =
            (Boolean(node.Cypher) && !node['isInGeneralSection']) || node.SubTypeId === ProjectDocFolderSubTypes.Change;

        const togglerOpts = togglerOptsRef.current.get(String(node.key));
        const isExpanded = togglerOpts?.expanded ?? false;
        const hasChildren = node.children && node.children.length > 0;
        const hasLeadingCritical = Boolean(
            criticalBeforeFileIcon && !isMobile && isTomOrAct && node.icon && criticalFolderMap?.get(Number(node.key))
        );
        const criticalRowClass = hasLeadingCritical
            ? ` pda-node-row--critical-before${hasChildren ? ' pda-node-row--critical-children' : ''}`
            : '';

        const toggler = (
            <button
                type="button"
                className={`pda-toggler${isExpanded ? ' pda-toggler--expanded' : ''}${hasLeadingCritical && hasChildren ? ' pda-toggler--below-critical' : ''}`}
                style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                onClick={(e) => {
                    e.stopPropagation();
                    togglerOpts?.onClick(e);
                }}
            >
                <i className="fa-solid fa-chevron-right" />
            </button>
        );

        const icon = node.icon ? <i className={`${node.icon} pda-node-icon`} /> : null;
        const displayedIcon = hasLeadingCritical ? (
            <span className="pda-node-icon-slot">
                {icon}
                <span
                    className={`pda-node-critical-before${node.bagdeValue ? ' pda-node-critical-before--with-status' : ''}`}
                >
                    {criticalBadge(node)}
                </span>
            </span>
        ) : (
            icon
        );

        if (nodeHasCypher) {
            const mode = criticalFolderMap?.get(Number(node.key));
            const criticalStyle =
                isMobile && mode
                    ? {
                          color:
                              mode === CriticalNoteModeEnum.Active
                                  ? 'var(--danger-color)'
                                  : mode === CriticalNoteModeEnum.Warranty
                                    ? 'var(--accent-orange)'
                                    : 'var(--text-tertiary)'
                      }
                    : undefined;
            return (
                <div className={`pda-node-row pda-node-row--has-cypher${criticalRowClass}`} style={oldStyle}>
                    {/* col 1, row 1: toggler + icon */}
                    <div className="pda-node-left">
                        {toggler}
                        {displayedIcon}
                    </div>
                    {/* col 2, row 1: cypher */}
                    <div className="pda-node-left">
                        {node.Cypher && !node['isInGeneralSection'] && (
                            <span
                                className="cypher"
                                style={
                                    cypherColor
                                        ? {
                                              color: cypherColor,
                                              borderColor: 'color-mix(in srgb, currentColor 40%, transparent)'
                                          }
                                        : undefined
                                }
                            >
                                {node.Cypher}
                            </span>
                        )}
                        {node.SubTypeId === ProjectDocFolderSubTypes.Change && (
                            <span className="cypher">изм-{node['OrderNumber']}</span>
                        )}
                    </div>

                    {/* col 3, row 1: actions */}
                    <div className="pda-node-right">
                        {showAfterNodeValue && afterNodeValue(node)}
                        <div className="d-inline edit-btn-wrapper">{editNodeBtn(node)}</div>
                    </div>
                    <div />
                    <div className="pda-node-label" style={criticalStyle}>
                        {node.label}
                        {!hasLeadingCritical && criticalBadge(node)}
                    </div>
                    {isTomOrAct && node.bagdeValue && (
                        <>
                            <div />
                            <div className="pda-node-status">
                                <span className={node.bagdeClass}>{node.bagdeValue}</span>
                            </div>
                        </>
                    )}
                </div>
            );
        }

        return (
            <div className={`pda-node-row${criticalRowClass}`} style={oldStyle}>
                {toggler}
                {displayedIcon}
                <div className="pda-node-main">{nodeVal(node, !hasLeadingCritical)}</div>
                <div className="pda-node-right">
                    {showAfterNodeValue && afterNodeValue(node)}
                    <div className="d-inline edit-btn-wrapper">{editNodeBtn(node)}</div>
                </div>
            </div>
        );
    };
    const onContextMenu = (e: TreeEventNodeEvent) => {
        if (canEditNode) {
            if (!canEditNode(e.node as PDATreeNode)) return null;
        }
        onContextMenuClick(e.originalEvent, e.node as PDATreeNode);
    };

    const onDragDrop = (e: TreeDragDropEvent) => {
        const draggedNode = e.dragNode as PDATreeNode;
        if (
            draggedNode.FolderTypeId === DocTemplateFolderTypeVals.Section &&
            draggedNode.ParentId != null &&
            ALL_SYSTEM_FOLDER_TITLES.includes(draggedNode.label ?? '')
        ) {
            return;
        }
        const [fromCoords, toCoords] = getDragDropNodeCoords(treeNodes, e.value as PDATreeNode[]);
        prepareCoords(fromCoords, toCoords, e.value as PDATreeNode[]);

        let currentNode: PDATreeNode | null = null;
        for (let i = 0; i < fromCoords.length - 1; i++) {
            currentNode = currentNode != null ? currentNode.children[fromCoords[i]] : treeNodes[fromCoords[i]];
        }

        const fromCoord: number = fromCoords[fromCoords.length - 1];
        const toCoord: number = toCoords[toCoords.length - 1];

        const ids: number[] = (currentNode?.children ?? treeNodes).map((x) => Number(x.key || 0)) ?? [];
        const deletedIds: number[] = ids.splice(fromCoord, 1);
        ids.splice(toCoord, 0, ...deletedIds);

        onPositionsChanged(ids);
    };

    const getDragDropNodeCoords = (before: PDATreeNode[], after: PDATreeNode[]): [number[], number[]] => {
        const fromCoords: number[] = [];
        const toCoords: number[] = [];

        getDragDropNodeCoordsFromRecursive(before, after, fromCoords);
        getDragDropNodeCoordsToRecursive(before, after, toCoords);

        return [fromCoords, toCoords];
    };

    const getDragDropNodeCoordsFromRecursive = (
        before: PDATreeNode[],
        after: PDATreeNode[],
        fromCoords: number[]
    ): boolean => {
        const initialLength: number = fromCoords.length;
        fromCoords.push(0);
        for (let i = 0; i < before.length; i++) {
            fromCoords[fromCoords.length - 1] = i;
            if (after.length < i + 1) return true;

            const beforeItem = before[i];
            const afterItem = after[i];
            if (beforeItem.key === afterItem.key) {
                const isToFinded: boolean = getDragDropNodeCoordsFromRecursive(
                    beforeItem.children,
                    afterItem.children,
                    fromCoords
                );
                if (isToFinded) return true;

                continue;
            }

            if (before.length > i + 1 && before[i + 1].key === afterItem.key) return true;
        }

        if (fromCoords.length > initialLength) fromCoords.splice(initialLength - 1, fromCoords.length - initialLength);

        return false;
    };

    const getDragDropNodeCoordsToRecursive = (
        before: PDATreeNode[],
        after: PDATreeNode[],
        toCoords: number[]
    ): boolean => {
        const initialLength: number = toCoords.length;
        toCoords.push(0);
        for (let i = 0; i < before.length; i++) {
            toCoords[toCoords.length - 1] = i;
            if (after.length < i + 1) return true;

            const beforeItem = before[i];
            const afterItem = after[i];
            if (beforeItem.key === afterItem.key) {
                const isFromFinded: boolean = getDragDropNodeCoordsToRecursive(
                    beforeItem.children,
                    afterItem.children,
                    toCoords
                );
                if (isFromFinded) return true;

                continue;
            }

            if (i > 0 && beforeItem.key === after[i - 1].key) {
                const index = after.findIndex((e) => e.children.filter((x) => x.key === before[i - 1].key).length > 0);
                if (index >= 0) {
                    toCoords[toCoords.length - 1] = index + 1;
                    toCoords.push(after[index].children.findIndex((x) => x.key === before[i - 1].key));
                }

                return index >= 0;
            }
        }

        if (before.length < after.length) return true;

        if (toCoords.length > initialLength) toCoords.splice(initialLength - 1, toCoords.length - initialLength);

        return false;
    };

    const prepareCoords = (fromCoords: number[], toCoords: number[], after: PDATreeNode[]): void => {
        for (let i = 0; i < fromCoords.length && i < toCoords.length; i++) {
            if (fromCoords[i] === toCoords[i]) continue;

            const toCoordsCurrentOriginal = toCoords[i];
            toCoords.splice(0, toCoords.length, ...fromCoords);

            if (i === fromCoords.length - 1) toCoords[toCoords.length - 1] = toCoordsCurrentOriginal;
            else
                toCoords[toCoords.length - 1] =
                    fromCoords[i] > toCoordsCurrentOriginal ? 0 : computeLastNodeIndex(after, fromCoords);

            return;
        }

        toCoords.splice(0, toCoords.length, ...fromCoords);
        toCoords[toCoords.length - 1] = 0;
    };

    const computeLastNodeIndex = (nodes: PDATreeNode[], coords: number[]): number => {
        let currentNode: PDATreeNode | null = null;
        for (let i = 0; i < coords.length - 1; i++) {
            currentNode = (currentNode != null ? currentNode.children : nodes)[coords[i]];
        }

        return currentNode != null ? currentNode.children.length : 0;
    };

    const criticalBadge = (node: PDATreeNode) => {
        const mode = criticalFolderMap?.get(Number(node.key));
        if (!mode) return null;
        if (isMobile) return null;

        const color =
            mode === CriticalNoteModeEnum.Active
                ? 'var(--danger-color)'
                : mode === CriticalNoteModeEnum.Warranty
                  ? 'var(--accent-orange)'
                  : 'var(--text-tertiary)';
        const tooltip =
            mode === CriticalNoteModeEnum.Active
                ? 'Содержит критические замечания'
                : mode === CriticalNoteModeEnum.Warranty
                  ? 'Критическое замечание принято как гарантийное'
                  : 'Критические замечания отработаны';

        return (
            <>
                <Tooltip target={`.critical-badge-${node.key}`} content={tooltip} position="top" />
                <i className={`fa-solid fa-triangle-exclamation critical-badge-${node.key}`} style={{ color }} />
            </>
        );
    };

    return (
        <React.Fragment>
            {/* <ContextMenu model={nodeMenuItems.filter(n => n.type !== MenuItemValues.AddTom)} ref={cmNodeChildNodeRef} />
      <ContextMenu model={nodeMenuItems.filter(n => n.type !== MenuItemValues.AddNode)} ref={cmNodeChildTomRef} /> */}
            <ContextMenu model={nodeMenuItems} ref={cmNodeRef} />
            <ContextMenu model={leafMenuItems} ref={cmLeafRef} />
            <ContextMenu model={workTypeMenuItems} ref={cmWorkTypeRef} />
            <ContextMenu model={actMenuItems} ref={cmActRef} />
            <ContextMenu model={actDocumentMenuItems} ref={cmActDocumentRef} />
            <ContextMenu model={actDocumentSectionMenuItems} ref={cmActDocumentSectionRef} />
            <ContextMenu model={actSectionMenuItems} ref={cmActSectionRef} />

            <TreeComponent
                {...treeComponentProps}
                dragdropScope="TreeComponent"
                onDragDrop={onDragDrop}
                className={`pda-tree-editor ${props.className ?? ``}`}
                value={treeNodes}
                //node template
                nodeTemplate={defaultNodeTemplate}
                togglerTemplate={togglerTemplate}
                //contextmenu
                onContextMenu={onContextMenu}
            />
        </React.Fragment>
    );
};
